import {
  clampRetries,
  clampRetryDelayMs,
  clampTimeoutMs,
  jsonHeaders,
  normalizeBaseUrl,
  openResponseWithRetry,
  ProviderHttpError,
  readJsonBody,
  readSSEData,
  resolveDeps,
  streamHeaders,
  toProviderError,
  type OpenResponseResult,
} from './http';
import { OpenAICompatibleAdapter } from './openai';
import type { ChatRequest, ProviderAdapter, ProviderDeps, ResolvedProviderConfig, StreamChunk, StreamUsage } from './types';

export function responsesUrl(baseUrl: string): string {
  const url = new URL(normalizeBaseUrl(baseUrl));
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/responses`;
  return url.toString();
}

export function buildResponsesBody(req: ChatRequest): Record<string, unknown> {
  const input: Record<string, unknown>[] = [];
  if (!req.messages.some(message => message.role === 'system' && message.content.trim()) && req.settings.systemPrompt?.trim()) {
    input.push({ role: 'system', content: req.settings.systemPrompt });
  }
  for (const message of req.messages) {
    if (message.role === 'tool') {
      if (!message.toolCallId) throw new ProviderHttpError('Responses 工具结果缺少关联调用 ID', 'config');
      input.push({ type: 'function_call_output', call_id: message.toolCallId, output: message.content });
      continue;
    }
    if (message.content || message.images?.length || !message.toolCalls?.length) input.push({
      role: message.role,
      content: message.role === 'user' && message.images?.length
        ? [
          ...(message.content ? [{ type: 'input_text', text: message.content }] : []),
          ...message.images.map(image_url => ({ type: 'input_image', image_url })),
        ]
        : message.content,
    });
    for (const call of message.toolCalls ?? []) {
      input.push({ type: 'function_call', call_id: call.id, name: call.name, arguments: call.arguments });
    }
  }
  const body: Record<string, unknown> = {
    model: req.model,
    input,
    stream: req.settings.stream,
    store: false,
    max_output_tokens: req.settings.maxOutputTokens,
  };
  if (req.settings.thinkingLevel !== 'off') {
    body.reasoning = { effort: req.settings.thinkingLevel, summary: 'auto' };
  } else if (!/^(?:o[1-9](?:[.-]|$)|gpt-5(?:[.-]|$))/i.test(req.model)) {
    body.temperature = req.settings.temperature;
    body.top_p = req.settings.topP;
  }
  if (req.tools?.length) {
    body.tools = req.tools.map(tool => ({ type: 'function', name: tool.name, description: tool.description, parameters: tool.inputSchema, strict: false }));
  }
  return body;
}

interface ResponseItem {
  type?: string;
  id?: string;
  call_id?: string;
  name?: string;
  arguments?: string;
  content?: { type?: string; text?: string; refusal?: string }[];
  summary?: { type?: string; text?: string }[];
}
interface ResponseShape {
  status?: string;
  output?: ResponseItem[];
  output_text?: string;
  usage?: { input_tokens?: number; output_tokens?: number };
  error?: { message?: string } | string | null;
  incomplete_details?: { reason?: string } | null;
}
interface ResponseEvent {
  type?: string;
  delta?: string;
  output_index?: number;
  item_id?: string;
  item?: ResponseItem;
  response?: ResponseShape;
  error?: { message?: string } | string;
  message?: string;
}

function usageOf(value?: ResponseShape['usage']): StreamUsage | undefined {
  return value ? { promptTokens: value.input_tokens ?? 0, completionTokens: value.output_tokens ?? 0 } : undefined;
}

function responseError(response: ResponseShape): StreamChunk | undefined {
  if (response.error || response.status === 'failed' || response.status === 'cancelled') {
    return { type: 'error', code: 'http', error: typeof response.error === 'string' ? response.error : response.error?.message ?? 'Responses 请求失败' };
  }
  if (response.status === 'incomplete') {
    return { type: 'error', code: 'http', error: `Responses 回答未完成：${response.incomplete_details?.reason ?? 'unknown'}` };
  }
  return undefined;
}

function itemChunks(item: ResponseItem, index: number): StreamChunk[] {
  if (item.type === 'function_call') {
    return [{ type: 'tool_call', delta: JSON.stringify({ index, id: item.call_id ?? item.id, name: item.name, arguments: item.arguments ?? '' }) }];
  }
  if (item.type === 'reasoning') {
    return (item.summary ?? []).filter(part => typeof part.text === 'string').map(part => ({ type: 'reasoning', delta: part.text }));
  }
  return (item.content ?? []).flatMap(part => {
    const text = part.type === 'refusal' ? part.refusal : part.text;
    return typeof text === 'string' ? [{ type: 'text' as const, delta: text }] : [];
  });
}

export function mapResponsesResponse(value: unknown): StreamChunk[] {
  if (!value || typeof value !== 'object') return [{ type: 'error', code: 'parse', error: 'Responses 响应格式无效' }];
  const response = value as ResponseShape;
  const chunks = Array.isArray(response.output) ? response.output.flatMap(itemChunks) : [];
  if (!chunks.length && typeof response.output_text === 'string') chunks.push({ type: 'text', delta: response.output_text });
  const error = responseError(response);
  if (error) return [...chunks, error];
  if (!Array.isArray(response.output) && typeof response.output_text !== 'string') return [{ type: 'error', code: 'parse', error: 'Responses 响应缺少 output' }];
  const usage = usageOf(response.usage);
  return [...chunks, usage ? { type: 'done', usage } : { type: 'done' }];
}

export class ResponsesAdapter extends OpenAICompatibleAdapter implements ProviderAdapter {
  private readonly responsesConfig: ResolvedProviderConfig;
  private readonly responsesDeps: ReturnType<typeof resolveDeps>;

  constructor(config: ResolvedProviderConfig, deps?: ProviderDeps) {
    super(config, deps);
    const resolved = resolveDeps(deps);
    this.responsesConfig = config;
    this.responsesDeps = {
      ...resolved,
      timeoutMs: clampTimeoutMs(config.timeoutMs, resolved.timeoutMs),
      retries: clampRetries(config.retries, resolved.retries),
      retryDelayMs: clampRetryDelayMs(config.retryDelayMs, resolved.retryDelayMs),
      extraHeaders: { ...resolved.extraHeaders, ...config.headers },
    };
  }

  override async *chat(req: ChatRequest): AsyncIterable<StreamChunk> {
    if (req.signal?.aborted) return;
    let opened: OpenResponseResult | undefined;
    try {
      const headers = req.settings.stream ? streamHeaders : jsonHeaders;
      opened = await openResponseWithRetry({
        url: responsesUrl(this.responsesConfig.baseUrl),
        init: { method: 'POST', headers: headers(this.responsesConfig.apiKey, this.responsesDeps.extraHeaders), body: JSON.stringify(buildResponsesBody(req)) },
        deps: this.responsesDeps,
        externalSignal: req.signal,
      });
      const { response, guard } = opened;
      if (!req.settings.stream || /^application\/json(?:;|$)/i.test(response.headers.get('content-type') ?? '')) {
        const value = await readJsonBody(response);
        if (req.signal?.aborted) return;
        for (const chunk of mapResponsesResponse(value)) yield chunk;
        return;
      }
      if (!response.body) throw new ProviderHttpError('响应没有可读流', 'parse');

      // Responses uses output indices for both messages and tools. Preserve these indices
      // so interleaved tool argument deltas merge into the same caller-owned tool record.
      const calls = new Map<number, { id?: string; name?: string; arguments: string; started: boolean }>();
      const seenItems = new Set<number>();
      for await (const payload of readSSEData(response.body, guard.signal)) {
        if (req.signal?.aborted) return;
        guard.reset();
        if (!payload.trim() || payload.trim() === '[DONE]') continue;
        let event: ResponseEvent;
        try { event = JSON.parse(payload) as ResponseEvent; }
        catch { throw new ProviderHttpError('Responses 流数据格式无效', 'parse'); }
        if (!event || typeof event !== 'object') throw new ProviderHttpError('Responses 流事件格式无效', 'parse');
        const index = event.output_index ?? 0;
        if (event.type === 'error') {
          yield { type: 'error', code: 'http', error: event.message ?? (typeof event.error === 'string' ? event.error : event.error?.message) ?? 'Responses 返回错误' };
          return;
        }
        if (event.type === 'response.failed' || event.type === 'response.incomplete' || event.type === 'response.completed') {
          const terminal = { ...event.response, status: event.type.slice('response.'.length) };
          const error = responseError(terminal);
          if (error) yield error;
          else {
            const usage = usageOf(terminal.usage);
            yield usage ? { type: 'done', usage } : { type: 'done' };
          }
          return;
        }
        if (event.type === 'response.output_text.delta' || event.type === 'response.refusal.delta' || event.type === 'response.reasoning_summary_text.delta' || event.type === 'response.reasoning_text.delta') {
          seenItems.add(index);
          if (event.delta) yield { type: event.type.includes('reasoning') ? 'reasoning' : 'text', delta: event.delta };
        }
        if (event.type === 'response.output_item.added' && event.item?.type === 'function_call') {
          const call = { id: event.item.call_id ?? event.item.id, name: event.item.name, arguments: event.item.arguments ?? '', started: true };
          calls.set(index, call);
          yield { type: 'tool_call', delta: JSON.stringify({ index, id: call.id, name: call.name, arguments: call.arguments }) };
        }
        if (event.type === 'response.function_call_arguments.delta') {
          const call = calls.get(index) ?? { arguments: '', started: false };
          call.arguments += event.delta ?? '';
          calls.set(index, call);
          yield { type: 'tool_call', delta: JSON.stringify({ index, arguments: event.delta ?? '' }) };
        }
        if (event.type === 'response.output_item.done' && event.item) {
          if (event.item.type === 'function_call') {
            const call = calls.get(index);
            const full = event.item.arguments ?? '';
            const remaining = call && full.startsWith(call.arguments) ? full.slice(call.arguments.length) : call ? '' : full;
            if (!call?.started || remaining) yield { type: 'tool_call', delta: JSON.stringify({ index, id: event.item.call_id ?? event.item.id, name: event.item.name, arguments: remaining }) };
          } else if (!seenItems.has(index)) {
            for (const chunk of itemChunks(event.item, index)) yield chunk;
          }
        }
      }
      if (!req.signal?.aborted) throw new ProviderHttpError('Responses 流在完成事件前中断', 'parse');
    } catch (error) {
      if (req.signal?.aborted) return;
      if (opened?.guard.timedOut()) {
        yield { type: 'error', code: 'timeout', error: `请求超时（${this.responsesDeps.timeoutMs}ms）` };
      } else {
        const normalized = toProviderError(error);
        yield { type: 'error', error: normalized.message, code: normalized.code, status: normalized.status };
      }
    } finally {
      opened?.guard.abort();
      opened?.guard.dispose();
    }
  }
}

export function createResponsesAdapter(config: ResolvedProviderConfig, deps?: ProviderDeps): ProviderAdapter {
  return new ResponsesAdapter(config, deps);
}
